import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Input,
  Label,
  FormFeedback,
  Spinner,
} from 'reactstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../helpers/api';
import logo from '../../assets/images/white logo png.png';

const InviteAccept = () => {
  document.title = 'Accept Invitation | Arcmedix';
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);

  // User form
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (token) {
      validateToken(token);
    } else {
      setError('Invalid invitation link. Please use the link from your invitation email.');
      setLoading(false);
    }
  }, [token]);

  const validateToken = async (tokenValue) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`invitation/validate?token=${tokenValue}`);
      setInvitation(data.response);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitLoading(true);
    setError(null);
    try {
      const response = await api.post('invitation/accept', { token, password });
      if (response.status === 200) {
        const responseData = response.data.response;
        // Same shape the login saga stores: the full session payload (activeOrganizationId,
        // currency, default work-order view, visibility scope, memberships...) WITHOUT the
        // token — the backend set it in the HttpOnly cookies. Persisting only a handful of
        // fields left activeOrganizationId undefined, so every org-scoped page loaded empty.
        const { token: _token, refresh_token: _refreshToken, ...userData } = responseData;
        localStorage.setItem(
          'authUser',
          JSON.stringify({ ...userData, permissions: userData.permissions || [] })
        );
        navigate(responseData.role === 'ROLE_REQUESTOR' ? '/service-requests' : '/reactive');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderForm = () => (
    <>
      <div className="text-center mb-4">
        <h5 className="text-primary">Accept Invitation</h5>
        {invitation?.organizationName && (
          <p className="text-muted">
            You've been invited to join <strong>{invitation.organizationName}</strong>
          </p>
        )}
      </div>
      <div className="mb-3">
        <Label className="form-label">Password <span className="text-danger">*</span></Label>
        <Input
          type="password"
          placeholder="Enter password (min 8 characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          invalid={password.length > 0 && password.length < 8}
        />
        {password.length > 0 && password.length < 8 && (
          <FormFeedback>Password must be at least 8 characters</FormFeedback>
        )}
      </div>
      <div className="mb-3">
        <Label className="form-label">Confirm Password <span className="text-danger">*</span></Label>
        <Input
          type="password"
          placeholder="Confirm your password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          invalid={confirmPassword.length > 0 && password !== confirmPassword}
        />
        {confirmPassword.length > 0 && password !== confirmPassword && (
          <FormFeedback>Passwords do not match</FormFeedback>
        )}
      </div>
      <div className="mt-4 text-center">
        <button
          className="btn btn-primary w-sm"
          onClick={handleAccept}
          disabled={submitLoading}
        >
          {submitLoading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Joining...
            </>
          ) : (
            'Join Organization'
          )}
        </button>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="authentication-bg min-vh-100 d-flex align-items-center justify-content-center">
        <div className="bg-overlay"></div>
        <Spinner color="light" />
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="authentication-bg min-vh-100">
        <div className="bg-overlay"></div>
        <Container>
          <div className="d-flex flex-column min-vh-100 px-3">
            <Row className="justify-content-center my-auto">
              <Col md={8} lg={6} xl={5}>
                <div className="text-center">
                  <Link to="/">
                    <img src={logo} alt="" height="90" width="200" />
                  </Link>
                </div>
                <Card>
                  <CardBody className="p-4">
                    <div className="p-2">
                      {error && (
                        <Alert color="danger">
                          <div className="d-flex align-items-center">
                            <i className="bx bx-error-circle me-2"></i>
                            <div>{error}</div>
                          </div>
                        </Alert>
                      )}

                      {invitation && renderForm()}

                      <div className="mt-4 text-center">
                        <p className="mb-0">
                          Already have an account?{' '}
                          <Link to="/login" className="text-primary fw-semibold">
                            Login
                          </Link>
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <div className="text-center text-muted p-4">
                  <p className="text-white-50">
                    Copyright &copy; {new Date().getFullYear()} Arcmedix. All rights reserved
                  </p>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default InviteAccept;
